// src/components/core/footer.ts

import { createElement, getElementByIdSafe } from "../../utils/domHelpers";
import { getAuthToken } from "../../services/authService"; // To check login state
import { logout } from "../../services/authService"; // Import logout function
import { showLoginOffCanvas } from "../modals/authModal"; // Import modal function

/**
 * Creates and appends the main application footer.
 * Links shown depend on user login status.
 * Assumes Bootstrap 5 classes are available.
 */
export function createFooter(): void {
	// Remove existing footer if it exists
	const existingFooter = document.querySelector("footer#app-footer");
	if (existingFooter) {
		existingFooter.remove();
	}

	const footer = createElement("footer", {
		id: "app-footer",
		className: "py-3 mt-auto", // Use margin-top: auto
	});
	const nav = createElement("ul", {
		className: "nav justify-content-center border-bottom pb-3 mb-3",
	});

	const isLoggedIn = !!getAuthToken(); // Check if token exists

	// Define navigation items
	const navItems: { text: string; href: string; onclick?: () => void }[] = [
		{ text: "Home", href: "/" },
	];

	if (isLoggedIn) {
		navItems.push(
			{ text: "Dashboard", href: "/dashboard" },
			{ text: "Entries", href: "/dashboard/entries" },
			{ text: "Logout", href: "#", onclick: () => logout() } // Call logout service function
		);
	} else {
		navItems.push(
			{ text: "Login", href: "#", onclick: showLoginOffCanvas } // Call modal function
		);
	}

	// Common links
	navItems.push(
		{ text: "Our Course Partners", href: "/courses" },
		{ text: "Contact", href: "/contact-us" },
		{ text: "Privacy Policy", href: "/privacy" },
		{ text: "Website Terms and Conditions", href: "/terms" },
		{ text: "Frequently Asked Questions", href: "/frequently-asked-questions" },
		{ text: "Competition(s) Terms", href: "/competition-terms" }
	);

	// Create list items and links
	navItems.forEach((item) => {
		const li = createElement("li", { className: "nav-item" });
		const a = createElement(
			"a",
			{
				className: "nav-link px-2 text-white",
				textContent: item.text,
				attributes: { href: item.href },
			},
			li
		);

		if (item.onclick) {
			a.addEventListener("click", (e) => {
				e.preventDefault(); // Prevent default link behavior
				item.onclick!(); // Execute the function
			});
		}
		nav.appendChild(li);
	});

	footer.appendChild(nav);

	// Social media section
	const socialSection = createElement("div", {
		className: "text-center mb-3",
	});

	const socialText = createElement("p", {
		className: "text-white mb-2",
	});
	socialText.innerHTML =
		"<strong>Follow our socials to keep up to date</strong>";

	const socialLinks = createElement("div", {
		className: "d-flex gap-3 justify-content-center",
	});

	// Social media links
	const socials = [
		{
			icon: "fab fa-instagram fa-lg",
			url: "https://www.instagram.com/ace_society_golf/",
		},
		{
			icon: "fab fa-facebook fa-lg",
			url: "https://www.facebook.com/acesocietygolf",
		},
		{
			icon: "fab fa-youtube fa-lg",
			url: "https://www.youtube.com/@AceSocietyGolf",
		},
	];

	socials.forEach((social) => {
		const link = createElement("a", {
			className: "text-white",
			attributes: { href: social.url, target: "_blank" },
		});
		link.innerHTML = `<i class="${social.icon}"></i>`;
		socialLinks.appendChild(link);
	});

	socialSection.appendChild(socialText);
	socialSection.appendChild(socialLinks);
	footer.appendChild(socialSection);

	// Copyright
	const currentYear = new Date().getFullYear();
	createElement(
		"p",
		{
			className: "text-center text-white",
			textContent: `© Ace Society ${currentYear}`,
		},
		footer
	);

	// Append footer to the body
	document.body.appendChild(footer);
}
